import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { EyeOff, GripVertical, Pencil, Plus, Trash2 } from "lucide-react";

import {
  AdminPage,
  ImageField,
  Loading,
  SortableCard,
  SortableGrid,
  SortableGridItem,
  SortableList,
  useRows,
  type Row,
} from "@/components/admin/AdminKit";
import { PartnersPreview } from "@/components/admin/PartnersPreview";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Slider } from "@/components/ui/slider";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

export const Route = createFileRoute("/_authenticated/admin/parteneri")({
  component: PartnersAdmin,
});

type PartnerForm = {
  id?: string;
  name: string;
  website: string;
  logo_url: string;
  tier_id: string;
  logo_scale: number;
  container_scale: number;
  visible: boolean;
  sort_order: number;
};

type TierForm = {
  id?: string;
  name: string;
  logo_size: string;
  visible: boolean;
  sort_order: number;
};

const sizeMap: Record<string, string> = {
  sm: "Mic",
  md: "Mediu",
  lg: "Mare",
  xl: "Foarte mare",
};

function sizeLabel(size: string) {
  return sizeMap[size] ?? size;
}

function TierPill({
  label,
  value,
  variant = "default",
}: {
  label: string;
  value: string;
  variant?: "default" | "success" | "muted";
}) {
  const variants = {
    default: "bg-primary/10 text-primary",
    success: "bg-emerald-500/15 text-emerald-600 dark:text-emerald-400",
    muted: "bg-muted text-muted-foreground",
  };
  return (
    <span className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-medium ${variants[variant]}`}>
      <span className="opacity-70">{label}:</span>
      {value}
    </span>
  );
}

function PartnersAdmin() {
  const tiers = useRows("partner_tiers");
  const partners = useRows("partners");
  const [editing, setEditing] = useState<PartnerForm | null>(null);
  const [editingTier, setEditingTier] = useState<TierForm | null>(null);

  if (tiers.isLoading || partners.isLoading) return <Loading />;

  const groups = [
    ...tiers.rows.map((t) => ({ id: t.id, name: String(t["name"] ?? "") })),
    { id: "", name: "Fără categorie" },
  ];

  function newPartner(tierId: string, count: number): PartnerForm {
    return {
      name: "",
      website: "",
      logo_url: "",
      tier_id: tierId,
      logo_scale: 1,
      container_scale: 1,
      visible: true,
      sort_order: count,
    };
  }

  return (
    <AdminPage
      title="Parteneri"
      description="Gestionează categoriile și siglele partenerilor: grupare, drag & drop, mărime individuală."
    >
      <Tabs defaultValue="logos">
        <TabsList>
          <TabsTrigger value="logos">Sigle parteneri</TabsTrigger>
          <TabsTrigger value="tiers">Categorii</TabsTrigger>
          <TabsTrigger value="preview">Previzualizare live</TabsTrigger>
        </TabsList>

        <TabsContent value="preview" className="mt-6">
          <PartnersPreview
            tiers={tiers.rows}
            partners={partners.rows}
            onSaveScales={async (changes) => {
              for (const c of changes) {
                await partners.save({
                  id: c.id,
                  ...(c.logo_scale !== undefined ? { logo_scale: c.logo_scale } : {}),
                  ...(c.container_scale !== undefined
                    ? { container_scale: c.container_scale }
                    : {}),
                });
              }
            }}
          />
        </TabsContent>

        <TabsContent value="logos" className="mt-6 space-y-10">
          {groups.map((group) => {
            const list = partners.rows.filter((p) => String(p["tier_id"] ?? "") === group.id);
            if (group.id === "" && list.length === 0) return null;
            return (
              <section key={group.id || "none"} className="space-y-3">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <h2 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
                    {group.name}{" "}
                    <span className="ml-1 text-xs font-normal">({list.length})</span>
                  </h2>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => setEditing(newPartner(group.id, list.length))}
                  >
                    <Plus className="mr-2 h-4 w-4" /> Adaugă partener
                  </Button>
                </div>
                {list.length === 0 ? (
                  <p className="rounded-xl border border-dashed border-border p-6 text-sm text-muted-foreground">
                    Niciun partener în această categorie.
                  </p>
                ) : (
                    <SortableGrid
                    items={list}
                    onReorder={partners.reorder}
                    className="grid gap-4 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 2xl:grid-cols-6"
                  >
                    {(item) => (
                      <SortableGridItem key={item.id} id={item.id}>
                        {(handle) => (
                          <PartnerCard
                            item={item}
                            handle={handle}
                            onEdit={() =>
                              setEditing({
                                id: item.id,
                                name: String(item["name"] ?? ""),
                                website: String(item["website"] ?? ""),
                                logo_url: String(item["logo_url"] ?? ""),
                                tier_id: String(item["tier_id"] ?? ""),
                                logo_scale: Number(item["logo_scale"] ?? 1) || 1,
                                container_scale: Number(item["container_scale"] ?? 1) || 1,
                                visible: Boolean(item["visible"]),
                                sort_order: Number(item["sort_order"] ?? 0),
                              })
                            }
                            onDelete={() => partners.remove(item.id)}
                          />
                        )}
                      </SortableGridItem>
                    )}
                  </SortableGrid>
                )}
              </section>
            );
          })}
        </TabsContent>

        <TabsContent value="tiers" className="mt-6 space-y-3">
          <SortableList items={tiers.rows} onReorder={tiers.reorder}>
            {(tier) => (
              <SortableCard key={tier.id} id={tier.id}>
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div>
                    <p className="font-semibold">{String(tier["name"] ?? "")}</p>
                    <div className="mt-1.5 flex flex-wrap items-center gap-2">
                      <TierPill label="Dimensiune de bază" value={sizeLabel(String(tier["logo_size"] ?? "md"))} />
                      <TierPill label="Ordine" value={String(tier["sort_order"] ?? 0)} />
                      <TierPill
                        label="Status"
                        value={tier["visible"] ? "Vizibilă" : "Ascunsă"}
                        variant={tier["visible"] ? "success" : "muted"}
                      />
                      <TierPill
                        label="Parteneri"
                        value={`${partners.rows.filter((p) => p["tier_id"] === tier.id).length} sigle`}
                      />
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {!tier["visible"] && <EyeOff className="h-4 w-4 text-muted-foreground" />}
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() =>
                        setEditingTier({
                          id: tier.id,
                          name: String(tier["name"] ?? ""),
                          logo_size: String(tier["logo_size"] ?? "md"),
                          visible: Boolean(tier["visible"]),
                          sort_order: Number(tier["sort_order"] ?? 0),
                        })
                      }
                    >
                      <Pencil className="mr-2 h-3.5 w-3.5" /> Editează
                    </Button>
                    <Button variant="ghost" size="icon" onClick={() => tiers.remove(tier.id)}>
                      <Trash2 className="h-4 w-4 text-destructive" />
                    </Button>
                  </div>
                </div>
              </SortableCard>
            )}
          </SortableList>
          <Button
            variant="outline"
            onClick={() =>
              setEditingTier({
                name: "",
                logo_size: "md",
                visible: true,
                sort_order: tiers.rows.length,
              })
            }
          >
            <Plus className="mr-2 h-4 w-4" /> Adaugă categorie
          </Button>
        </TabsContent>
      </Tabs>

      <PartnerDialog
        form={editing}
        tiers={tiers.rows}
        onClose={() => setEditing(null)}
        onSave={async (form) => {
          const ok = await partners.save({
            ...(form.id ? { id: form.id } : {}),
            name: form.name,
            website: form.website || null,
            logo_url: form.logo_url || null,
            tier_id: form.tier_id === "" ? null : form.tier_id,
            logo_scale: form.logo_scale,
            container_scale: form.container_scale,
            visible: form.visible,
            sort_order: form.sort_order,
          });
          if (ok) setEditing(null);
        }}
      />

      <TierDialog
        form={editingTier}
        onClose={() => setEditingTier(null)}
        onSave={async (form) => {
          const ok = await tiers.save({
            ...(form.id ? { id: form.id } : {}),
            name: form.name,
            slug: form.name.toLowerCase().replace(/[^a-z0-9]+/g, "-") || "categorie",
            logo_size: form.logo_size,
            visible: form.visible,
            sort_order: form.sort_order,
          });
          if (ok) setEditingTier(null);
        }}
      />
    </AdminPage>
  );
}

function PartnerCard({
  item,
  handle,
  onEdit,
  onDelete,
}: {
  item: Row;
  handle: { attributes: object; listeners: object | undefined };
  onEdit: () => void;
  onDelete: () => void;
}) {
  const logo = item["logo_url"] ? String(item["logo_url"]) : "";
  const scale = Number(item["logo_scale"] ?? 1) || 1;
  const boxScale = Number(item["container_scale"] ?? 1) || 1;

  return (
    <div className="group relative flex h-full flex-col rounded-2xl border border-border bg-card p-4 shadow-sm">
      <div className="flex items-start justify-between">
        <button
          type="button"
          aria-label="Reordonează"
          className="cursor-grab rounded-md p-1 text-muted-foreground hover:bg-muted active:cursor-grabbing"
          {...handle.attributes}
          {...(handle.listeners ?? {})}
        >
          <GripVertical className="h-4 w-4" />
        </button>
        <div className="flex items-center gap-1">
          {!item["visible"] && <EyeOff className="h-4 w-4 text-muted-foreground" />}
          <Button variant="ghost" size="icon" onClick={onEdit} aria-label="Editează">
            <Pencil className="h-4 w-4" />
          </Button>
          <Button variant="ghost" size="icon" onClick={onDelete} aria-label="Șterge">
            <Trash2 className="h-4 w-4 text-destructive" />
          </Button>
        </div>
      </div>

      <div className="mt-2 flex h-28 items-center justify-center overflow-hidden rounded-xl bg-white p-3">
        {logo ? (
          <span
            className="flex items-center justify-center overflow-hidden"
            style={{ height: `${boxScale * 100}%`, width: `${boxScale * 100}%` }}
          >
            <img
              src={logo}
              alt={String(item["name"] ?? "")}
              style={{ transform: `scale(${scale})` }}
              className="max-h-full max-w-full object-contain"
            />
          </span>
        ) : (
          <span className="text-xs text-muted-foreground">Fără siglă</span>
        )}
      </div>

      <div className="mt-3">
        <p className="truncate text-sm font-semibold">{String(item["name"] ?? "")}</p>
        <div className="mt-1 flex flex-wrap gap-1">
          <span className="inline-flex items-center rounded-full bg-primary/10 px-2 py-0.5 text-[11px] font-medium text-primary">
            Siglă: {Math.round(scale * 100)}%
          </span>
          <span className="inline-flex items-center rounded-full bg-muted px-2 py-0.5 text-[11px] font-medium text-muted-foreground">
            Spațiu: {Math.round(boxScale * 100)}%
          </span>
        </div>
      </div>
    </div>
  );
}

function PartnerDialog({
  form,
  tiers,
  onClose,
  onSave,
}: {
  form: PartnerForm | null;
  tiers: Row[];
  onClose: () => void;
  onSave: (form: PartnerForm) => Promise<void>;
}) {
  const [draft, setDraft] = useState<PartnerForm | null>(form);
  const [key, setKey] = useState(0);
  if (form !== null && draft === null) {
    setDraft(form);
    setKey((k) => k + 1);
  }
  if (form === null && draft !== null) setDraft(null);
  if (!draft) return null;
  const set = (patch: Partial<PartnerForm>) => setDraft((d) => (d ? { ...d, ...patch } : d));

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent key={key} className="max-h-[90vh] overflow-y-auto sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>{draft.id ? "Editează partener" : "Partener nou"}</DialogTitle>
          <DialogDescription>
            Încarcă sigla și ajustează mărimea afișării ei pe site.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-5">
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <Label>Nume</Label>
              <Input value={draft.name} onChange={(e) => set({ name: e.target.value })} />
            </div>
            <div className="space-y-2">
              <Label>Website</Label>
              <Input value={draft.website} onChange={(e) => set({ website: e.target.value })} />
            </div>
          </div>

          <div className="space-y-2">
            <Label>Categorie</Label>
            <Select value={draft.tier_id} onValueChange={(v) => set({ tier_id: v })}>
              <SelectTrigger>
                <SelectValue placeholder="Alege categoria" />
              </SelectTrigger>
              <SelectContent>
                {tiers.map((t) => (
                  <SelectItem key={t.id} value={t.id}>
                    {String(t["name"])}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <ImageField label="Siglă" value={draft.logo_url} onChange={(url) => set({ logo_url: url })} />

          <div className="space-y-4 rounded-xl border border-border p-4">
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label>Mărime siglă (imaginea)</Label>
                <span className="text-sm font-semibold">{Math.round(draft.logo_scale * 100)}%</span>
              </div>
              <Slider
                value={[draft.logo_scale]}
                min={0.4}
                max={2}
                step={0.05}
                onValueChange={([v]) => set({ logo_scale: v ?? 1 })}
              />
              <p className="text-xs text-muted-foreground">
                Cât de mare apare sigla în spațiul ei, fără să schimbe layout-ul.
              </p>
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label>Mărime spațiu (containerul)</Label>
                <span className="text-sm font-semibold">
                  {Math.round(draft.container_scale * 100)}%
                </span>
              </div>
              <Slider
                value={[draft.container_scale]}
                min={0.4}
                max={2}
                step={0.05}
                onValueChange={([v]) => set({ container_scale: v ?? 1 })}
              />
              <p className="text-xs text-muted-foreground">
                Cât loc ocupă sigla în rând — micșorează ca să încapă mai multe pe un rând.
              </p>
            </div>

            <div className="flex h-28 items-center justify-center overflow-hidden rounded-lg bg-white">
              {draft.logo_url ? (
                <span
                  className="flex items-center justify-center overflow-hidden"
                  style={{
                    height: `${draft.container_scale * 70}%`,
                    width: `${draft.container_scale * 70}%`,
                  }}
                >
                  <img
                    src={draft.logo_url}
                    alt=""
                    style={{ transform: `scale(${draft.logo_scale})` }}
                    className="max-h-full max-w-full object-contain"
                  />
                </span>
              ) : (
                <span className="text-xs text-muted-foreground">Previzualizare siglă</span>
              )}
            </div>
          </div>

          <label className="flex items-center gap-2 text-sm">
            <Switch checked={draft.visible} onCheckedChange={(v) => set({ visible: v })} />
            Vizibil pe site
          </label>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Renunță
          </Button>
          <Button onClick={() => void onSave(draft)}>Salvează</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function TierDialog({
  form,
  onClose,
  onSave,
}: {
  form: TierForm | null;
  onClose: () => void;
  onSave: (form: TierForm) => Promise<void>;
}) {
  const [draft, setDraft] = useState<TierForm | null>(form);
  const [key, setKey] = useState(0);
  if (form !== null && draft === null) {
    setDraft(form);
    setKey((k) => k + 1);
  }
  if (form === null && draft !== null) setDraft(null);
  if (!draft) return null;
  const set = (patch: Partial<TierForm>) => setDraft((d) => (d ? { ...d, ...patch } : d));

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent key={key} className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{draft.id ? "Editează categoria" : "Categorie nouă"}</DialogTitle>
          <DialogDescription>Numele apare ca etichetă deasupra siglelor.</DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-2">
            <Label>Denumire</Label>
            <Input value={draft.name} onChange={(e) => set({ name: e.target.value })} />
          </div>
          <div className="space-y-2">
            <Label>Mărime logo (categorie)</Label>
            <Select value={draft.logo_size} onValueChange={(v) => set({ logo_size: v })}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="sm">Mic</SelectItem>
                <SelectItem value="md">Mediu</SelectItem>
                <SelectItem value="lg">Mare</SelectItem>
                <SelectItem value="xl">Foarte mare</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <label className="flex items-center gap-2 text-sm">
            <Switch checked={draft.visible} onCheckedChange={(v) => set({ visible: v })} />
            Vizibilă
          </label>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Renunță
          </Button>
          <Button onClick={() => void onSave(draft)}>Salvează</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
